export const VIEW_MODE_OPTIONS = Object.freeze({
  CREATE: 'create',
  DETAILS: 'details',
  EDIT: 'edit',
});

export const FORM_INPUT_TYPES = Object.freeze({
  DISABLED: 'disabled',
  TEXT: 'text',
  SELECT: 'select',
  DROPDOWN: 'dropdown',
  TEXT_AREA: 'textarea',
});

export const PAGE_ROUTES = Object.freeze({
  LOGIN: '/',
  LANDING_PAGE: '/landing-page',
  TEMPLATE_EDITOR: '/template-editor',
  TEMPLATES: '/templates',
  NEW_TEMPLATE: '/new-template',
  CUSTOM_EMAIL: '/custom-email',
  EMAIL_SENT: '/email-sent',
  OTHER: '/*',
});

export const EDITOR_RESOLVERS = Object.freeze({
  EMAIL: {
    CONTAINER: 'EmailContainer',
    CONTAINER_ROW: 'EmailContainerRow',
    CONTAINER_COLUMN: 'EmailContainerColumn',
    TEMPLATE: 'TemplateEmail',
    PARAGRAPH: 'EmailParagraph',
  },
  PDF: {
    CONTAINER: 'PdfContainer',
    TEMPLATE: 'TemplatePdf',
    GRID_CELL: 'GridCell',
    TEXT_INPUT: 'Input',
    PAGE_HEADER: 'PageHeader',
    PAGE_FOOTER: 'PageFooter',
    PAGE_BODY: 'PageBody',
    PAGE_LAYOUT: 'PageLayout',
    CARDS: 'Cards',
    PARAGRAPH: 'PdfParagraph',
  },
  COMMON: {
    BUTTON: 'Button',
    PLACEHOLDER: 'Placeholder',
    CANVAS_IMAGE: 'CanvasImage',
    TABLE_CELL: 'TableCell',
    TABLE_ROW: 'TableRow',
    TABLE_HEADER: 'TableHeader',
    TABLE_FOOTER: 'TableFooter',
    TABLE: 'Table',
  },
});

export const API_STATUSES = Object.freeze({
  OK: 200,
  FORBIDDEN: 403,
  INTERNAL_SERVER_ERROR: 500,
});

export const TABLE_SEGMENT_OPTIONS = {
  SHOW_BORDER: {
    name: 'showBorder',
    iconName: 'showBorder',
    label: 'show_border',
  },
  SHOW_HEADER: {
    name: 'showHeader',
    iconName: 'showHeader',
    label: 'show_header',
  },
  SHOW_FOOTER: {
    name: 'showFooter',
    iconName: 'showFooter',
    label: 'show_footer',
  },
  ADD_COLUMN: {
    name: 'addColumn',
    iconName: 'addColumn',
    label: 'add_column',
  },
  REMOVE_COLUMN: {
    name: 'removeColumn',
    iconName: 'removeColumn',
    label: 'remove_column',
  },
  ADD_ROW: {
    name: 'addRow',
    iconName: 'addRow',
    label: 'add_row',
  },
  REMOVE_ROW: {
    name: 'removeRow',
    iconName: 'removeRow',
    label: 'remove_row',
  },
};

export const TABLE_SEGMENT_OPTION_LIST = [
  TABLE_SEGMENT_OPTIONS.SHOW_BORDER,
  TABLE_SEGMENT_OPTIONS.SHOW_HEADER,
  TABLE_SEGMENT_OPTIONS.SHOW_FOOTER,
  TABLE_SEGMENT_OPTIONS.ADD_COLUMN,
  TABLE_SEGMENT_OPTIONS.REMOVE_COLUMN,
  TABLE_SEGMENT_OPTIONS.ADD_ROW,
  TABLE_SEGMENT_OPTIONS.REMOVE_ROW,
];

export const RECIPIENT_OPTION = Object.freeze({
  type: {},
  email: '',
});

export const TEMPLATE_PAGE_SIZE_OPTIONS = Object.freeze([25, 50, 100]);

export const FONT_FAMILY_OPTIONS = Object.freeze([
  { key: 'Arial', value: 'Arial', label: 'Arial' },
  { key: 'Verdana', value: 'Verdana', label: 'Verdana' },
  { key: 'Tahoma', value: 'Tahoma', label: 'Tahoma' },
  { key: 'Trebuchet MS', value: 'Trebuchet MS', label: 'Trebuchet MS' },
  { key: 'Georgia', value: 'Georgia', label: 'Georgia' },
  {
    key: 'Times New Roman',
    value: 'Times New Roman',
    label: 'Times New Roman',
  },
  { key: 'Monospace', value: 'Monospace', label: 'Monospace' },
  { key: 'Courier New', value: 'Courier New', label: 'Courier New' },
  { key: 'Garamond', value: 'Garamond', label: 'Garamond' },
  {
    key: 'Brush Script MT',
    value: 'Brush Script MT',
    label: 'Brush Script MT',
  },
  { key: 'Helvetica', value: 'Helvetica', label: 'Helvetica' },
]);

export const TEMPLATE_PAGINATION_MODEL = Object.freeze({
  pageSize: 25,
  page: 0,
});

export const NEW_TEMPLATE_STEPS = Object.freeze({
  DETAILS: 0,
  SUBJECT_AND_BODY: 1,
  PREVIEW: 2,
});

export const IMAGE_LIST_CONFIG = {
  rowHeight: 120,
  rowWeight: 248,
  fit: 'crop',
  auto: 'format',
};

export const TEMPLATES_FILTER_MAPPING = Object.freeze({
  id: 'templateId',
  name: 'templateName',
  dataTypeName: 'dataTypeName',
  templateType: 'templateType',
  isActive: 'isActive',
});

export const TEMPLATE_ACTIVE_STATUS = Object.freeze({
  ACTIVE: 'active',
  ARCHIVED: 'archived',
});

export const MEDIA_CONFIG = Object.freeze({
  MAX_SIZE_IN_MB: 5,
  get MAX_SIZE_IN_BYTES() {
    return this.MAX_SIZE_IN_MB * 1024 * 1024;
  },
});

export const TEMPLATE_DELETION_STATUSES = Object.freeze({
  NONE: 'NONE',
  SOFT: 'SOFT',
  HARD: 'HARD',
});

export const EXPORT_CONFIG = Object.freeze({
  MAX_TEMPLATES_TO_EXPORT: 10,
  MAX_SIZE_IN_MB: 10,
});

export const TEMPLATE_TYPES = Object.freeze({
  EMAIL: 'email',
  PDF_STYLED: 'pdf_styled',
  PDF_PROGRAMMATIC: 'pdf_programmatic',
});

export const PDF_FORMAT_OPTIONS = Object.freeze([
  {
    id: 'A3',
    name: 'A3',
    widthMm: 297,
    heightMm: 420,
    portrait: {
      min: {
        headerMm: 12,
        footerMm: 10,
      },
      standard: {
        headerMm: 15,
        footerMm: 12,
      },
    },
    landscape: {
      min: {
        headerMm: 10,
        footerMm: 8,
      },
      standard: {
        headerMm: 12,
        footerMm: 12,
      },
    },
  },
  {
    id: 'A4',
    name: 'A4',
    widthMm: 210,
    heightMm: 297,
    portrait: {
      min: {
        headerMm: 10,
        footerMm: 8,
      },
      standard: {
        headerMm: 12,
        footerMm: 12,
      },
    },
    landscape: {
      min: {
        headerMm: 8,
        footerMm: 6,
      },
      standard: {
        headerMm: 10,
        footerMm: 10,
      },
    },
  },
  {
    id: 'A5',
    name: 'A5',
    widthMm: 148,
    heightMm: 210,
    portrait: {
      min: {
        headerMm: 6,
        footerMm: 5,
      },
      standard: {
        headerMm: 10,
        footerMm: 8,
      },
    },
    landscape: {
      min: {
        headerMm: 5,
        footerMm: 4,
      },
      standard: {
        headerMm: 8,
        footerMm: 6,
      },
    },
  },
  {
    id: 'Letter',
    name: 'Letter',
    widthMm: 216,
    heightMm: 279,
    portrait: {
      min: {
        headerMm: 10,
        footerMm: 8,
      },
      standard: {
        headerMm: 12,
        footerMm: 10,
      },
    },
    landscape: {
      min: {
        headerMm: 8,
        footerMm: 6,
      },
      standard: {
        headerMm: 12,
        footerMm: 10,
      },
    },
  },
  {
    id: 'Legal',
    name: 'Legal',
    widthMm: 216,
    heightMm: 356,
    portrait: {
      min: {
        headerMm: 10,
        footerMm: 7,
      },
      standard: {
        headerMm: 12,
        footerMm: 10,
      },
    },
    landscape: {
      min: {
        headerMm: 8,
        footerMm: 6,
      },
      standard: {
        headerMm: 12,
        footerMm: 10,
      },
    },
  },
  {
    id: 'Tabloid',
    name: 'Tabloid',
    widthMm: 279,
    heightMm: 432,
    portrait: {
      min: {
        headerMm: 12,
        footerMm: 10,
      },
      standard: {
        headerMm: 15,
        footerMm: 12,
      },
    },
    landscape: {
      min: {
        headerMm: 10,
        footerMm: 8,
      },
      standard: {
        headerMm: 12,
        footerMm: 10,
      },
    },
  },
]);

export const PDF_ORIENTATION_OPTIONS = Object.freeze([
  { id: 'portrait', name: 'Portrait', label: 'portrait' },
  { id: 'landscape', name: 'Landscape', label: 'landscape' },
]);

export const MODAL_ACTIONS = Object.freeze({
  CALC: 'calc',
  SUM: 'sum',
  SOURCE: 'source',
});

export const INIT_PLACEHOLDER_MODAL_STATE = Object.freeze({
  show: false,
  listSource: '',
  parentListSrc: '',
  parent: '',
  showRootFields: true,
  action: null,
});

export const INIT_TEXT_MODAL_STATE = Object.freeze({
  show: false,
  listSource: '',
  parent: '',
  showRootFields: true,
  action: null,
  selections: {
    range: null,
    selection: null,
    selectedElement: null,
    selectedId: null,
  },
});

export const INIT_TABLE_MODAL_STATE = Object.freeze({
  show: false,
  parentListSrc: undefined,
});

export const INIT_GRID_CELL_MODAL_STATE = Object.freeze({
  show: false,
  listSource: '',
  parent: '',
  showRootFields: true,
  action: null,
});

export const TEMPLATE_TYPE_OPTIONS = [
  { id: TEMPLATE_TYPES.EMAIL, name: 'Email', label: 'email' },
  { id: TEMPLATE_TYPES.PDF_STYLED, name: 'Styled PDF', label: 'pdf_styled' },
  // { id: TEMPLATE_TYPES.PDF_PROGRAMMATIC, name: 'Programmatic PDF' },
];

export const H_ALIGN_OPTIONS = [
  {
    name: 'left',
    iconName: 'left',
    iconSize: 'small',
    label: 'left',
  },
  {
    name: 'center',
    iconName: 'center-h',
    iconSize: 'small',
    label: 'center',
  },
  {
    name: 'right',
    iconName: 'right',
    iconSize: 'small',
    label: 'right',
  },
  {
    name: 'justify',
    iconName: 'justify',
    iconSize: 'small',
    label: 'justify',
  },
];

export const BORDER_OPTIONS = [
  {
    name: 'borderTop',
    iconName: 'top',
    iconSize: 'small',
    label: 'top',
  },
  {
    name: 'borderRight',
    iconName: 'right',
    iconSize: 'small',
    label: 'right',
  },
  {
    name: 'borderBottom',
    iconName: 'bottom',
    iconSize: 'small',
    label: 'bottom',
  },
  {
    name: 'borderLeft',
    iconName: 'left',
    iconSize: 'small',
    label: 'left',
  },
  {
    name: 'borderAll',
    iconName: 'all',
    iconSize: 'small',
    label: 'border_all',
  },
];

export const V_ALIGN_OPTIONS = [
  {
    name: 'top',
    iconName: 'top',
    iconSize: 'small',
    label: 'top',
  },
  {
    name: 'middle',
    iconName: 'center',
    iconSize: 'small',
    label: 'center',
  },
  {
    name: 'bottom',
    iconName: 'bottom',
    iconSize: 'small',
    label: 'bottom',
  },
];

export const FLEX_ALIGN_OPTIONS = [
  {
    name: 'flex-start',
    iconName: 'left',
    iconSize: 'small',
    label: 'left',
  },
  {
    name: 'center',
    iconName: 'center-h',
    iconSize: 'small',
    label: 'center-h',
  },
  {
    name: 'flex-end',
    iconName: 'right',
    iconSize: 'small',
    label: 'right',
  },
];

export const FLEX_JUSTIFY_CONTENT_OPTIONS = [
  {
    name: 'flex-start',
    iconName: 'top',
    iconSize: 'small',
    label: 'left',
  },
  {
    name: 'center',
    iconName: 'center',
    iconSize: 'small',
    label: 'center',
  },
  {
    name: 'flex-end',
    iconName: 'bottom',
    iconSize: 'small',
    label: 'right',
  },
];

export const FLEX_DIRECTION_OPTIONS = [
  {
    name: 'row',
    iconName: 'display-horizontal',
    iconSize: 'small',
    label: 'display-horizontal',
  },
  {
    name: 'column',
    iconName: 'display-vertical',
    iconSize: 'small',
    label: 'display-vertical',
  },
];

export const FLEX_ORDER_OPTIONS = [
  {
    name: 'normal',
    iconName: 'normal-order',
    iconSize: 'small',
    label: 'normal-order',
  },
  {
    name: 'reverse',
    iconName: 'reverse-order',
    iconSize: 'small',
    label: 'reverse-order',
  },
];

export const V_FLEX_ORDER_OPTIONS = [
  {
    name: 'normal',
    iconName: 'normal-order-v',
    iconSize: 'small',
    label: 'normal-order',
  },
  {
    name: 'reverse',
    iconName: 'reverse-order-v',
    iconSize: 'small',
    label: 'reverse-order',
  },
];

export const AVAILABLE_LANGS = Object.freeze([
  { value: 'en', label: 'english' },
  { value: 'de', label: 'german' },
]);

export const VIEW_MODE_OPTIONS_2 = Object.freeze({
  DETAILS: 0,
  CREATE: 1,
  EDIT_FORM: 2,
  EDIT_TEMPLATE: 3,
});
